import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Box, Typography, Button, Chip, CircularProgress } from '@mui/material';
import { Stop, FiberManualRecord, Terrain, Timeline, ViewInAr, Terminal, ExpandMore, ExpandLess, ShowChart } from '@mui/icons-material';
import { vortxColors, retroColors, vortxTypography, useAuth } from '@vortx/shared-ui';
import SimulationPage from '../../pages/SimulationPage';

const LoadProfileTerrain = React.lazy(() =>
    import('../../../../vortx-shared-ui/src/data-display/LoadProfileTerrain')
);

import { useTwinScenarios } from '../../hooks/useTwinScenarios';
import { useSimulationStatus, useSimulationRuns, killSimulation } from '../../hooks/useSimulationStatus';
import useSimulationWebSocket from '../../hooks/useSimulationWebSocket';
import SimulationSetup from './SimulationSetup';
import SimulationControls from './SimulationControls';
import EventInjector from './EventInjector';
import SiteScene from './SiteScene';
import HexGround from './HexGround';
import EnergyFlowConduits from './EnergyFlowConduits';
import EquipmentDetailPanel from './EquipmentDetailPanel';
import {
    SolarArray,
    BatteryContainer,
    EVCharger,
    ColdStorage,
    GridConnection,
    BuildingLoad,
} from './equipment';
import EquipmentLabel from './EquipmentLabel';
import LiveDispatchChart from './LiveDispatchChart';
import LogTerminal from './LogTerminal';
import { LAYOUT_POSITIONS } from './equipmentDefs';

const CLOUD_FUNCTION_URL =
    'https://us-central1-vortx-flux-site-dna-389f0.cloudfunctions.net/startSimulationVM';

const WS_PROXY_URL = 'wss://sim-ws-proxy-202472576296.us-central1.run.app/ws';

const DURATION_TO_DAYS = { '1w': 7, '1m': 30, '1y': 365 };
const STEPS_PER_DAY_CONST = 96;

// Equipment positions from shared defs (auto-layout)
const EQUIPMENT_POSITIONS = LAYOUT_POSITIONS;

const HISTORY_MAX = 48;
const STEPS_PER_HOUR = 4; // 15-min sim resolution → 4 ticks per hour

/**
 * Extract energy flows from simulation state for particle rendering.
 */
function extractFlows(simState) {
    if (!simState?.equipment) return [];

    const flows = [];
    const eq = simState.equipment;

    // Solar -> building or battery
    if (eq.solar?.power_kw > 0) {
        flows.push({ from: 'solar', to: 'building_load', kw: eq.solar.power_kw * 0.7 });
        if (eq.battery?.power_kw > 0) {
            flows.push({ from: 'solar', to: 'battery', kw: eq.solar.power_kw * 0.3 });
        }
    }

    // Battery discharge -> building
    if (eq.battery?.power_kw < 0) {
        flows.push({ from: 'battery', to: 'building_load', kw: Math.abs(eq.battery.power_kw) });
    }

    // Grid import -> building
    if (eq.grid?.power_kw > 0) {
        flows.push({ from: 'grid', to: 'building_load', kw: eq.grid.power_kw });
    }

    // Grid export (solar excess)
    if (eq.grid?.power_kw < 0) {
        flows.push({ from: 'solar', to: 'grid', kw: Math.abs(eq.grid.power_kw) });
    }

    // EV charging
    if (eq.ev_fleet?.power_kw > 0) {
        flows.push({ from: 'building_load', to: 'ev_fleet', kw: eq.ev_fleet.power_kw });
    }

    // Cold storage
    if (eq.cold_storage?.power_kw > 0) {
        flows.push({ from: 'building_load', to: 'cold_storage', kw: eq.cold_storage.power_kw });
    }

    return flows;
}

/**
 * ConnectionStatus - Indicator chip showing WebSocket connection state.
 */
function ConnectionStatus({ connected, error }) {
    const color = connected ? '#44ff88' : error ? '#ff4444' : '#888';
    const label = connected ? 'CONNECTED' : error ? 'ERROR' : 'DISCONNECTED';

    return (
        <Chip
            icon={<FiberManualRecord sx={{ fontSize: 10, color: `${color} !important` }} />}
            label={label}
            size="small"
            sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.6rem',
                fontWeight: 600,
                letterSpacing: '0.08em',
                color,
                backgroundColor: `${color}11`,
                border: `1px solid ${color}33`,
                height: 24,
            }}
        />
    );
}

/**
 * ModeSelector - Toggle between Annual Simulation and Real-Time Simulation.
 */
function ModeSelector({ mode, onModeChange }) {
    const modes = [
        { id: 'realtime', label: 'Real-Time Simulation', icon: ViewInAr },
        { id: 'annual', label: 'Annual Simulation', icon: Timeline },
    ];

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 2,
            py: 1,
            borderBottom: '1px solid rgba(178, 140, 80, 0.15)',
            backgroundColor: retroColors.surface,
        }}>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.6rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                color: retroColors.dimmed,
                mr: 1,
            }}>
                Mode
            </Typography>
            {modes.map(({ id, label, icon: Icon }) => {
                const isActive = mode === id;
                return (
                    <Box
                        key={id}
                        onClick={() => onModeChange(id)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            cursor: 'pointer',
                            borderRadius: 1,
                            border: `1px solid ${isActive ? vortxColors.gold : 'transparent'}`,
                            backgroundColor: isActive ? `${vortxColors.gold}18` : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: isActive ? `${vortxColors.gold}22` : `${vortxColors.gold}0a`,
                                border: `1px solid ${isActive ? vortxColors.gold : `${vortxColors.gold}40`}`,
                            },
                        }}
                    >
                        <Icon sx={{
                            fontSize: 14,
                            color: isActive ? vortxColors.gold : retroColors.dimmed,
                            transition: 'color 0.2s ease',
                        }} />
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.65rem',
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? vortxColors.gold : retroColors.dimmed,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            transition: 'all 0.2s ease',
                            textShadow: isActive ? `0 0 8px ${vortxColors.gold}40` : 'none',
                        }}>
                            {label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

/**
 * DigitalTwinTab - Full Digital Twin with mode selector.
 *
 * Modes:
 * - Real-Time: 3D viewport + VM controls (original Digital Twin)
 * - Annual: Batch simulation (SimulationPage)
 *
 * @param {{ projectId: string }} props
 */
export default function DigitalTwinTab({ projectId }) {
    const [mode, setMode] = useState('realtime');
    const { data: twinData, loading: scenariosLoading } = useTwinScenarios(projectId);
    const { currentUser } = useAuth();

    const { runs, loading: runsLoading } = useSimulationRuns({
        userId: currentUser?.uid,
        projectId,
    });

    // Track the active sim from Firestore for auto-reconnect
    const { activeSimulation, simStatus: firestoreSimStatus } = useSimulationStatus({
        userId: currentUser?.uid,
        projectId,
    });

    const handleKill = useCallback(async (simId) => {
        if (!currentUser?.uid) return;
        try {
            await killSimulation({ userId: currentUser.uid, projectId, simId });
        } catch (err) {
            console.error('[DigitalTwinTab] Failed to kill simulation:', err);
        }
    }, [currentUser, projectId]);

    const [isRunning, setIsRunning] = useState(false);
    const [isBooting, setIsBooting] = useState(false);
    const [bootError, setBootError] = useState(null);
    const [wsUrl, setWsUrl] = useState(null);
    const [simId, setSimId] = useState(null);
    const [selectedEquipment, setSelectedEquipment] = useState(null);
    const [showTerrain, setShowTerrain] = useState(false);
    const [showLogs, setShowLogs] = useState(false);
    const [showChart, setShowChart] = useState(true);
    const historyRef = useRef({});
    const terrainHistoryRef = useRef({ loads: [], agents: { solar: [], battery: [], ev: [], cold: [] } });
    const [terrainTick, setTerrainTick] = useState(0); // triggers re-render on terrain data growth
    const dispatchHistoryRef = useRef([]);
    const [dispatchTick, setDispatchTick] = useState(0); // triggers chart re-render
    const reconnectedRef = useRef(null); // tracks which sim_id we already reconnected to

    // Auto-reconnect disabled — was causing WebGL context lost loops
    // when connecting to stale sims after VM restart. TODO: re-enable
    // with WebSocket health check before mounting Canvas.

    // Derive app scenarios and project data from twin endpoint
    const appScenarios = twinData?.apps || {};
    const projectData = twinData?.project || null;

    // WebSocket connection (only active when we have a URL)
    const { state: simState, connected, send, error } = useSimulationWebSocket(wsUrl);

    // Accumulate metric history for sparklines + terrain
    useEffect(() => {
        if (!simState?.equipment) return;

        const eq = simState.equipment;

        // Sparkline history (last 48 samples)
        Object.entries(eq).forEach(([eqType, eqState]) => {
            if (!historyRef.current[eqType]) {
                historyRef.current[eqType] = {};
            }
            Object.entries(eqState).forEach(([key, val]) => {
                if (typeof val !== 'number') return;
                if (!historyRef.current[eqType][key]) {
                    historyRef.current[eqType][key] = [];
                }
                const arr = historyRef.current[eqType][key];
                arr.push(val);
                if (arr.length > HISTORY_MAX) arr.shift();
            });
        });

        // Dispatch chart: accumulate every tick for the live chart
        const tick = simState.tick ?? dispatchHistoryRef.current.length;
        dispatchHistoryRef.current.push({
            tick,
            sim_time: simState.sim_time,
            solar: eq.solar?.power_kw || 0,
            battery: eq.battery?.power_kw || 0,
            batt_soc: (eq.battery?.soc ?? 0) * 100,  // 0-100%
            ev: eq.ev_fleet?.power_kw || 0,
            cold: eq.cold_storage?.power_kw || 0,
            grid: Math.max(0, eq.grid?.power_kw || 0),
            load: eq.building_load?.power_kw || 0,
        });
        // Keep last 384 ticks (4 days) to avoid unbounded growth
        if (dispatchHistoryRef.current.length > 384) {
            dispatchHistoryRef.current = dispatchHistoryRef.current.slice(-384);
        }
        setDispatchTick(t => t + 1);

        // Terrain history: accumulate all 15-min ticks, downsample to hourly every 4 ticks
        const th = terrainHistoryRef.current;
        const buildingKw = eq.building_load?.power_kw || 0;
        th._raw = th._raw || [];
        th._raw.push(buildingKw);

        // Accumulate per-agent raw ticks
        th._rawAgents = th._rawAgents || { solar: [], battery: [], ev: [], cold: [] };
        th._rawAgents.solar.push(eq.solar?.power_kw || 0);
        th._rawAgents.battery.push(eq.battery?.power_kw || 0);
        th._rawAgents.ev.push(eq.ev_fleet?.power_kw || 0);
        th._rawAgents.cold.push(eq.cold_storage?.power_kw || 0);

        // Every 4 ticks (1 hour), downsample and push hourly value
        if (th._raw.length % STEPS_PER_HOUR === 0) {
            const start = th._raw.length - STEPS_PER_HOUR;
            // Take max within the hour for building load
            let maxKw = 0;
            for (let i = start; i < th._raw.length; i++) {
                if (th._raw[i] > maxKw) maxKw = th._raw[i];
            }
            th.loads.push(maxKw);

            // Agent loads: take max magnitude within the hour
            for (const key of ['solar', 'battery', 'ev', 'cold']) {
                let maxAgent = 0;
                for (let i = start; i < th._rawAgents[key].length; i++) {
                    const v = Math.abs(th._rawAgents[key][i]);
                    if (v > maxAgent) maxAgent = v;
                }
                th.agents[key].push(th._rawAgents[key][start] >= 0 ? maxAgent : -maxAgent);
            }

            // Trigger re-render for terrain (every full day = 24 hourly values)
            if (th.loads.length % 24 === 0) {
                setTerrainTick(t => t + 1);
            }
        }
    }, [simState]);

    const handleStart = useCallback(async ({ scenarioIds, duration, speed, startStep = 0 }) => {
        if (!currentUser?.uid) {
            setBootError('Not authenticated');
            return;
        }

        setIsBooting(true);
        setBootError(null);
        historyRef.current = {};
        terrainHistoryRef.current = { loads: [], agents: { solar: [], battery: [], ev: [], cold: [] } };
        setTerrainTick(0);
        dispatchHistoryRef.current = [];
        setDispatchTick(0);

        try {
            // Cloud Function boots VM, waits for health, calls /start
            const resp = await fetch(CLOUD_FUNCTION_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    project_id: projectId,
                    scenario_id: Object.values(scenarioIds)[0],
                    scenario_ids: scenarioIds,
                    user_id: currentUser.uid,
                    config: {
                        speed,
                        duration_days: DURATION_TO_DAYS[duration] || 30,
                        start_step: startStep,
                    },
                }),
            });

            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                throw new Error(errData.error || `Cloud Function returned ${resp.status}`);
            }

            const data = await resp.json();
            const vmIp = data.vm_ip;

            if (!vmIp) {
                throw new Error('Cloud Function did not return a VM IP address');
            }

            // Connect WebSocket via Cloud Run proxy (provides WSS from HTTPS page)
            setWsUrl(`${WS_PROXY_URL}?vm_ip=${vmIp}`);
            setSimId(data.sim_id);
            setIsRunning(true);
        } catch (err) {
            console.error('[DigitalTwinTab] Failed to start simulation:', err);
            setBootError(err.message);
        } finally {
            setIsBooting(false);
        }
    }, [projectId, currentUser]);

    const handleStop = useCallback(() => {
        send('stop');
        // Also mark as killed in Firestore directly — the WebSocket
        // disconnect may race with the server's Firestore update
        if (currentUser?.uid && simId) {
            killSimulation({ userId: currentUser.uid, projectId, simId }).catch(() => {});
        }
        setIsRunning(false);
        setWsUrl(null);
        setSimId(null);
        setSelectedEquipment(null);
        setShowTerrain(false);
        reconnectedRef.current = null;
        historyRef.current = {};
        terrainHistoryRef.current = { loads: [], agents: { solar: [], battery: [], ev: [], cold: [] } };
        setTerrainTick(0);
        dispatchHistoryRef.current = [];
        setDispatchTick(0);
    }, [send, currentUser, projectId, simId]);

    const handleEquipmentClick = useCallback((equipmentType) => (e) => {
        e.stopPropagation();
        setSelectedEquipment((prev) => prev === equipmentType ? null : equipmentType);
    }, []);

    const simTime = simState?.sim_time || null;
    const isNight = useMemo(() => {
        if (!simTime) return false;
        const hour = new Date(simTime).getHours();
        return hour < 6 || hour > 19;
    }, [simTime]);

    const flows = useMemo(() => extractFlows(simState), [simState]);
    const eqStates = simState?.equipment || {};

    // Terrain live data — snapshot on each terrainTick (every completed day)
    const terrainLiveData = useMemo(() => {
        const th = terrainHistoryRef.current;
        return {
            loads: [...th.loads],
            agentLoads: {
                solar: [...th.agents.solar],
                battery: [...th.agents.battery],
                ev: [...th.agents.ev],
                cold: [...th.agents.cold],
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [terrainTick]);

    // Dispatch chart data — snapshot on each dispatch tick
    const dispatchData = useMemo(() => [...dispatchHistoryRef.current],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatchTick]);

    // MPC horizon data from WebSocket state
    const horizonData = simState?.horizon || null;

    // ─── Annual Simulation Mode ─────────────────────────────────────────
    if (mode === 'annual') {
        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: retroColors.bg }}>
                <ModeSelector mode={mode} onModeChange={setMode} />
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <SimulationPage projectId={projectId} />
                </Box>
            </Box>
        );
    }

    // ─── Real-Time: Setup / Booting View ─────────────────────────────────
    if (!isRunning) {
        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: retroColors.bg }}>
                <ModeSelector mode={mode} onModeChange={setMode} />
                <Box sx={{ flex: 1 }}>
                    {isBooting ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            gap: 2,
                        }}>
                            <CircularProgress size={32} sx={{ color: vortxColors.gold }} />
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: vortxColors.gold,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                            }}>
                                Starting simulation...
                            </Typography>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                color: retroColors.dimmed,
                            }}>
                                Booting VM and starting simulation engine
                            </Typography>
                        </Box>
                    ) : (
                        <SimulationSetup
                            appScenarios={appScenarios}
                            projectName={twinData?.project?.name}
                            onStart={handleStart}
                            loading={scenariosLoading}
                            error={bootError}
                            runs={runs}
                            runsLoading={runsLoading}
                            onKill={handleKill}
                        />
                    )}
                </Box>
            </Box>
        );
    }

    // ─── Running View ────────────────────────────────────────────────
    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#000000',
        }}>
            {/* Header bar */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 0.8,
                borderBottom: `1px solid ${vortxColors.teal}44`,
                backgroundColor: retroColors.surface,
                minHeight: 40,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ConnectionStatus connected={connected} error={error} />
                    {simState?.status && (
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.65rem',
                            color: retroColors.dimmed,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        }}>
                            {simState.status}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        onClick={() => setShowTerrain(p => !p)}
                        startIcon={<Terrain sx={{ fontSize: 14 }} />}
                        size="small"
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: showTerrain ? vortxColors.gold : retroColors.dimmed,
                            borderColor: showTerrain ? `${vortxColors.gold}66` : `${retroColors.dimmed}33`,
                            '&:hover': {
                                borderColor: vortxColors.gold,
                                backgroundColor: `${vortxColors.gold}11`,
                            },
                        }}
                        variant="outlined"
                    >
                        Terrain
                    </Button>
                </Box>

                <Button
                    onClick={handleStop}
                    startIcon={<Stop sx={{ fontSize: 16 }} />}
                    size="small"
                    sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: vortxColors.error,
                        borderColor: `${vortxColors.error}44`,
                        '&:hover': {
                            borderColor: vortxColors.error,
                            backgroundColor: `${vortxColors.error}11`,
                        },
                    }}
                    variant="outlined"
                >
                    Stop
                </Button>
            </Box>

            {/* 3D Viewport */}
            <Box sx={{ flex: 1, minHeight: '35vh', position: 'relative', overflow: 'hidden' }}>
                <SiteScene simTime={simTime}>
                    <HexGround activeEquipment={Object.keys(eqStates)} />

                    {/* Equipment meshes */}
                    <SolarArray
                        state={eqStates.solar}
                        position={EQUIPMENT_POSITIONS.solar}
                        onClick={handleEquipmentClick('solar')}
                    />
                    <BatteryContainer
                        state={eqStates.battery}
                        position={EQUIPMENT_POSITIONS.battery}
                        onClick={handleEquipmentClick('battery')}
                    />
                    <EVCharger
                        state={eqStates.ev_fleet}
                        position={EQUIPMENT_POSITIONS.ev_fleet}
                        onClick={handleEquipmentClick('ev_fleet')}
                    />
                    <ColdStorage
                        state={eqStates.cold_storage}
                        position={EQUIPMENT_POSITIONS.cold_storage}
                        onClick={handleEquipmentClick('cold_storage')}
                    />
                    <GridConnection
                        state={eqStates.grid}
                        position={EQUIPMENT_POSITIONS.grid}
                        onClick={handleEquipmentClick('grid')}
                    />
                    <BuildingLoad
                        state={eqStates.building_load}
                        position={EQUIPMENT_POSITIONS.building_load}
                        onClick={handleEquipmentClick('building_load')}
                        isNight={isNight}
                    />

                    {/* Floating labels above each equipment */}
                    {Object.entries(EQUIPMENT_POSITIONS).map(([type, pos]) => (
                        <EquipmentLabel
                            key={`label-${type}`}
                            equipmentType={type}
                            state={eqStates[type]}
                            position={pos}
                        />
                    ))}

                    {/* Energy flow conduits */}
                    <EnergyFlowConduits
                        flows={flows}
                        equipmentPositions={EQUIPMENT_POSITIONS}
                    />
                </SiteScene>

                {/* Dispatch chart overlay removed — now a collapsible panel below */}

                {/* Equipment detail overlay */}
                {selectedEquipment && (
                    <EquipmentDetailPanel
                        equipment={eqStates[selectedEquipment]}
                        equipmentType={selectedEquipment}
                        history={historyRef.current[selectedEquipment]}
                        onClose={() => setSelectedEquipment(null)}
                    />
                )}

                {/* Live terrain overlay */}
                {showTerrain && terrainLiveData.loads.length >= 24 && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 10,
                        backgroundColor: 'rgba(10, 15, 10, 0.95)',
                    }}>
                        <React.Suspense fallback={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <CircularProgress size={24} sx={{ color: vortxColors.gold }} />
                            </Box>
                        }>
                            <LoadProfileTerrain
                                mode="live"
                                liveData={terrainLiveData}
                                interactive
                                isDark
                            />
                        </React.Suspense>
                    </Box>
                )}
            </Box>

            {/* Bottom controls */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderTop: `1px solid ${vortxColors.teal}44`,
            }}>
                <Box sx={{ flex: 1 }}>
                    <SimulationControls
                        state={simState}
                        connected={connected}
                        onSend={send}
                    />
                </Box>
                <Box sx={{ pr: 2 }}>
                    <EventInjector
                        onSend={send}
                        disabled={!connected || simState?.status !== 'running'}
                    />
                </Box>
            </Box>

            {/* Power flow chart — always visible */}
            <Box sx={{
                height: 280,
                minHeight: 280,
                borderTop: '1px solid rgba(178, 140, 80, 0.15)',
                backgroundColor: 'rgba(10, 15, 14, 0.95)',
                flexShrink: 0,
                overflow: 'hidden',
            }}>
                {dispatchData.length > 0 ? (
                    <LiveDispatchChart
                        history={dispatchData}
                        horizon={horizonData}
                    />
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: retroColors.dimmed,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                        }}>
                            Waiting for simulation data...
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Cost summary row */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                px: 2,
                py: 0.8,
                borderTop: '1px solid rgba(178, 140, 80, 0.1)',
                backgroundColor: retroColors.surface,
                flexShrink: 0,
            }}>
                <Box>
                    <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.45rem', color: retroColors.dimmed, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Energy Cost
                    </Typography>
                    <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.7rem', fontWeight: 600, color: vortxColors.gold }}>
                        ${(simState?.metrics?.total_cost_cumulative_usd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                </Box>
                <Box>
                    <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.45rem', color: retroColors.dimmed, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Demand Charge
                    </Typography>
                    <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.7rem', fontWeight: 600, color: '#f59e0b' }}>
                        ${(simState?.metrics?.demand_charge_month_usd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                </Box>
                <Box>
                    <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.45rem', color: retroColors.dimmed, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Peak Demand
                    </Typography>
                    <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.7rem', fontWeight: 600, color: '#e74c3c' }}>
                        {(simState?.metrics?.peak_kw_month ?? 0).toFixed(0)} kW
                    </Typography>
                </Box>
            </Box>

            {/* Engine log terminal — always visible */}
            <Box sx={{
                height: 80,
                borderTop: '1px solid rgba(95, 212, 196, 0.15)',
                backgroundColor: 'rgba(10, 12, 10, 0.95)',
                flexShrink: 0,
            }}>
                <LogTerminal logs={simState?.logs} />
            </Box>
        </Box>
    );
}
